import React, { useState } from "react";

import { Consumption, stockApi } from "@/client/api";
import { Button, FormField, Input, Modal } from "@/client/components/ui";
import { useToast } from "@/client/context/ToastContext";
import { DeleteIcon } from "@/client/icons";

function ExistingRow({
  c,
  onUpdated,
  onDeleted,
}: {
  c: Consumption;
  onUpdated: () => void;
  onDeleted: () => void;
}) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [servings, setServings] = useState(String(c.servings_consumed));
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    const n = parseFloat(servings);
    if (isNaN(n) || n <= 0) return;
    setSaving(true);
    try {
      await stockApi.updateConsumption(c.id, { servings_consumed: n });
      toast("Consumo actualizado");
      onUpdated();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await stockApi.deleteConsumption(c.id);
      toast("Consumo eliminado");
      onDeleted();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <Input value={servings} onChange={setServings} type="number" autoFocus className="w-20" />
        <span className="text-sm text-term-text-muted">porc.</span>
        <Button variant="primary" size="sm" onClick={() => void handleSave()} disabled={saving}>
          {saving ? "…" : "Guardar"}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
          Cancelar
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 group">
      <button
        onClick={() => setEditing(true)}
        className="flex-1 text-left text-sm text-term-text hover:text-term-accent cursor-pointer transition-colors"
      >
        {c.servings_consumed} {c.servings_consumed !== 1 ? "porciones" : "porción"}
      </button>
      <button
        onClick={() => void handleDelete()}
        disabled={saving}
        className="text-term-text-muted hover:text-term-error cursor-pointer transition-colors p-1 disabled:opacity-40"
        aria-label="Eliminar consumo"
      >
        <DeleteIcon width={14} height={14} />
      </button>
    </div>
  );
}

export function ConsumeModal({
  recipeName,
  day,
  stock,
  existing,
  onSubmit,
  onChanged,
  onClose,
}: {
  recipeName: string;
  day: string;
  stock: number;
  existing: Consumption[];
  onSubmit: (servings: number) => void;
  onChanged: () => void;
  onClose: () => void;
}) {
  const [servings, setServings] = useState("1");
  const hasExisting = existing.length > 0;

  return (
    <Modal
      title={hasExisting ? "Consumos" : "Registrar consumo"}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {hasExisting ? "Cerrar" : "Cancelar"}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const n = parseFloat(servings);
              if (n > 0) onSubmit(n);
            }}
            disabled={!servings || parseFloat(servings) <= 0}
          >
            Registrar
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const n = parseFloat(servings);
          if (n > 0) onSubmit(n);
        }}
        className="flex flex-col gap-3"
      >
        <div className="text-sm text-term-text">
          <span className="font-medium">{recipeName}</span>
          <span className="text-term-text-muted ml-2">— {day}</span>
        </div>

        {/* Existing consumption records */}
        {hasExisting && (
          <div className="flex flex-col gap-1.5 border border-term-border rounded-xl p-3">
            <div className="text-xs font-medium text-term-text-dim uppercase tracking-wide mb-1">
              Registrados
            </div>
            {existing.map((c) => (
              <ExistingRow key={c.id} c={c} onUpdated={onChanged} onDeleted={onChanged} />
            ))}
          </div>
        )}

        {stock > 0 ? (
          <div className="text-sm text-term-user">
            {stock} {stock !== 1 ? "porciones disponibles" : "porción disponible"}
          </div>
        ) : (
          <div className="text-sm text-term-error">Sin stock disponible</div>
        )}
        <FormField label={hasExisting ? "Agregar más" : "Porciones a consumir"}>
          <Input value={servings} onChange={setServings} type="number" placeholder="1" autoFocus />
        </FormField>
      </form>
    </Modal>
  );
}
